import "./style.css";
import { useState, useEffect } from "react"
import TitleBar from "../../layout/TitleBar"
import Layout from "../../layout/Layout"
import {
  getBusinessUsers,
  approveBusinessAccount,
  rejectBusinessAccount,
  deactivateBusinessAccount,
  updateUserPaymentSetting
} from "../../../actions/admin/user";
import { Link } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { useLoading } from "../../LoadingContext"
import { ON_GET_BUSINESS_USER } from "../../../actions/types";
import ConfirmationModal from "../components/ConfirmationModal"
import useConfirmation from "../components/useConfirmation"
import ModalSuccess from "../../modal/ModalSuccess"
import Spinner from "../../layout/Spinner";
import AccountInformation from "./_components/AccountInformation"
import BusinessInformation from "./_components/BusinessInformation"
import AuthorizedRepresentativeContacts from "./_components/AuthorizedRepresentativeContacts"
import EmailRecipients from "./_components/EmailRecipients"
import BillingInformation from "./_components/BillingInformation"
import PaymentMethod from "./_components/PaymentMethod"
import BusinessLicense from "./_components/BusinessLicense"
import { BackAltIcon, RejectAccountIcon, SendInquiryIcon } from "../../icons";

const BusinessDetail = ({ match }) => {
  const dispatch = useDispatch()
  const businessuser = useSelector((state) => state.businessuser);
  const { loading, businessUsers } = businessuser;
  const { showLoading, closeLoading } = useLoading()
  const { confirm, confirmationProps } = useConfirmation()
  const [showModalSuccess, setShowModalSuccess] = useState(false);
  const [modalSuccessContent, setModalSuccessContent] = useState({
    title: "",
    desc: ""
  })
  const [iataEligible, setIataEligible] = useState(false);
  const [iataDiscount, setIataDiscount] = useState(0);
  const [deferredPaymentEligible, setDeferredPaymentEligible] = useState(false);

  useEffect(() => {
    dispatch({
      type: ON_GET_BUSINESS_USER,
    });
    const handler = setTimeout(() => {
      dispatch(getBusinessUsers({
        sortBy: "asc",
        orderBy: "date",
        page: 1,
        limit: 1,
        disableCalculateTotal: false,
        draw: 1,
        id: match?.params?.id,
      }));
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [dispatch]);

  useEffect(() => {
    if(businessUsers?.length === 0) return
    setIataEligible(businessUsers?.data[0]?.iataEligible ?? false)
    setIataDiscount(businessUsers?.data[0]?.iataDiscount ?? 0)
    setDeferredPaymentEligible(businessUsers?.data[0]?.deferredPaymentEligible)
  }, [businessUsers])

  // One popup for every outcome: on failure the backend's message explains
  // why (e.g. "Cannot reject a business account whose status is ACTIVE").
  const showActionResultModal = (actionResponse, successContent, failureTitle) => {
    closeLoading(true);
    setShowModalSuccess(true);
    if (actionResponse?.status === "success") {
      setModalSuccessContent(successContent);
    } else {
      setModalSuccessContent({
        title: failureTitle,
        desc: actionResponse?.message ?? "",
      });
    }
  }

  const approveAccountFunc = async () => {
    showLoading(true);
    const approveAccountResponse = await approveBusinessAccount(businessUsers?.data[0]._id, {
      iataEligible: Boolean(iataEligible),
      iataDiscount: iataEligible ? Number(iataDiscount) : 0,
      deferredPaymentEligible: Boolean(deferredPaymentEligible)
    }, dispatch);

    showActionResultModal(approveAccountResponse, {
      title: `The business account for ${approveAccountResponse?.data?.businessName} has been successfully verified`,
      desc: "A verification email has been sent to the business."
    }, "Failed to Approve Account");
  }

  const verifyAccountButtonHandler = () => confirm({
    title: "Would you like to verify this business account?",
    description: "The business is emailed as soon as the account is verified.",
    confirmLabel: "Confirm Verification",
    cancelLabel: "Back",
    onConfirm: approveAccountFunc,
  })

  const rejectAccountFunc = async () => {
    showLoading(true);
    const rejectAccountResponse = await rejectBusinessAccount(businessUsers?.data[0]._id, dispatch);

    showActionResultModal(rejectAccountResponse, {
      title: `The business account for ${rejectAccountResponse?.data?.businessName} has been successfully declined`,
      desc: "An email has been sent to the business."
    }, "Failed to Decline Account");
  }

  const rejectAccountButtonHandler = () => confirm({
    title: "Would you like to decline this business account?",
    description: "The business is emailed that their account was declined.",
    variant: "danger",
    confirmLabel: "Decline Account",
    cancelLabel: "Back",
    onConfirm: rejectAccountFunc,
  })

  const deactiveAccountFunc = async () => {
    showLoading(true);
    const deactivateAccountResponse = await deactivateBusinessAccount(businessUsers?.data[0]._id, dispatch);

    showActionResultModal(deactivateAccountResponse, {
      title: `The business account for ${deactivateAccountResponse?.data?.businessName} has been successfully deactivated`,
      desc: "No email is sent to the business for a deactivation."
    }, "Failed to Deactivate Account");
  }

  const deactiveAccountButtonHandler = () => confirm({
    title: "Would you like to deactivate this business account?",
    description: "The business will no longer be able to use this account. No email is sent.",
    variant: "danger",
    confirmLabel: "Confirm Deactivation",
    cancelLabel: "Back",
    onConfirm: deactiveAccountFunc,
  })

  const handleCloseModalSuccess = () => {
    setShowModalSuccess(false)
    setModalSuccessContent({
      title: "",
      desc: ""
    })
  }

  const getAccountStatus = () => {
    if (loading) return {
      style: "",
      text: ""
    };

    switch (businessUsers?.data[0]?.status) {
      case "PENDING_VERIFICATION":
        return {
          style: "badge badge-pending-verif",
          text: "Pending Verification"
        }
        break;

      case "PENDING_APPROVAL":
        return {
          style: "badge badge-pending-approve",
          text: "Pending Approval"
        }
        break;

      case "ACTIVE":
        return {
          style: "badge badge-approved",
          text: "Approved"
        }
        break;

      case "REJECTED":
        return {
          style: "badge badge-rejected",
          text: "Rejected"
        }
        break;

      case "DEACTIVE":
        return {
          style: "badge badge-rejected",
          text: "Deactive"
        }
        break;

      default:
        break;
    }
  }

  const handleSetIataDiscount = (e) => {
    setIataDiscount(e.target.value);
  }

  const handleSetIataEligible = (data) => {
    setIataDiscount(0);
    setIataEligible(data);
  }

  const savedUser = businessUsers?.data?.[0];

  // Payment settings are chosen through Verify until the account is approved,
  // so the standalone "Update Payment Setting" button only appears afterwards.
  const isApprovedAccount = savedUser?.status === "ACTIVE";

  // A 0% rate on an "Eligible" account is meaningless (it also renders as
  // "0% discount" in the verification email), so block saving it.
  const isIataDiscountValid = !iataEligible
    || (Number(iataDiscount) > 0 && Number(iataDiscount) <= 100);

  const isPaymentSettingDirty = Boolean(savedUser) && (
    Boolean(savedUser.iataEligible) !== Boolean(iataEligible)
    || Boolean(savedUser.deferredPaymentEligible) !== Boolean(deferredPaymentEligible)
    || (Boolean(iataEligible) && Number(savedUser.iataDiscount ?? 0) !== Number(iataDiscount || 0))
  );

  const updatePaymentSettingFunc = async () => {
    showLoading(true);
    const updateResponse = await updateUserPaymentSetting(savedUser._id, {
      iataEligible: Boolean(iataEligible),
      iataDiscount: iataEligible ? Number(iataDiscount) : 0,
      deferredPaymentEligible: Boolean(deferredPaymentEligible)
    }, dispatch);

    closeLoading(true);

    if (updateResponse.desc === "SUCCESS" && updateResponse.code === 200) {
      setShowModalSuccess(true);
      setModalSuccessContent({
        title: "The payment setting has been successfully updated",
        desc: `Payment Type: ${updateResponse?.data?.deferredPaymentEligible ? "Deferred Payment・後払い" : "Advance Payment・前払い"} — Discount: ${updateResponse?.data?.iataEligible ? `${updateResponse?.data?.iataDiscount}%` : "Not Eligible"}`
      });
    } else {
      setShowModalSuccess(true);
      setModalSuccessContent({
        title: "Failed to Update Payment Setting",
        desc: updateResponse?.message ?? ""
      });
    }
  }

  const updatePaymentSettingButtonHandler = () => confirm({
    title: "Would you like to update the payment type and discount information of this account?",
    description: "This changes how every future booking on this account is billed.",
    confirmLabel: "Confirm Update",
    cancelLabel: "Back",
    onConfirm: updatePaymentSettingFunc,
  })

  return (
    <Layout>
      <ConfirmationModal {...confirmationProps} />
      <ModalSuccess
        handleClose={handleCloseModalSuccess}
        show={showModalSuccess}
        title={modalSuccessContent.title}
        desc={modalSuccessContent.desc}
      />
      <main className="col-md-9 ms-sm-auto col-lg-10 px-md-4 w-100">
        <div className="container-fluid mb-5">
          <div className="row d-flex align-items-center justify-content-between mt-3 py-3">
            <div className="col d-flex align-items-center justify-content-start gap-3">
              <Link to={`/admin/userlist-business`}>
                <BackAltIcon />
              </Link>
              <h4 className="m-0">
                {
                  !loading && (
                    businessUsers?.data[0]?.businessDetail?.businessInformation?.businessName
                  )
                }
              </h4>
            </div>
            <div className="col d-flex align-items-center justify-content-end gap-2">
              <div className={getAccountStatus().style}>{getAccountStatus().text}</div>
            </div>
          </div>

          {
            loading ? (
              <div className="container-fluid mb-5 tw:!mt-8">
                <Spinner />
              </div>
            ) : (
              <>
                <AccountInformation
                  loading={loading}
                  data={businessUsers?.data[0]}
                />

                <hr className="hr-grey-account p-0 m-0 hr-h-4" />

                <BusinessInformation
                  loading={loading}
                  data={businessUsers?.data[0]}
                />

                <hr className="hr-grey-account p-0 m-0 hr-h-4" />

                <AuthorizedRepresentativeContacts
                  loading={loading}
                  data={businessUsers?.data[0]}
                />

                <hr className="hr-grey-account p-0 m-0 hr-h-4" />

                <EmailRecipients
                  loading={loading}
                  data={businessUsers?.data[0]}
                />

                <hr className="hr-grey-account p-0 m-0 hr-h-4" />

                <BillingInformation
                  loading={loading}
                  data={businessUsers?.data[0]}
                />

                <hr className="hr-grey-account p-0 m-0 hr-h-4" />

                <PaymentMethod
                  loading={loading}
                  data={businessUsers?.data[0]}
                  iataEligible={iataEligible}
                  setIataEligible={handleSetIataEligible}
                  iataDiscount={iataDiscount}
                  setIataDiscount={handleSetIataDiscount}
                  deferredPaymentEligible={deferredPaymentEligible}
                  setDeferredPaymentEligible={setDeferredPaymentEligible}
                  onUpdate={isApprovedAccount ? updatePaymentSettingButtonHandler : undefined}
                  canUpdate={isPaymentSettingDirty && isIataDiscountValid}
                  updateDisabledReason={
                    !isIataDiscountValid
                      ? "Enter a discount rate between 1 and 100"
                      : "No changes to save"
                  }
                />

                <hr className="hr-grey-account p-0 m-0 hr-h-4" />

                <BusinessLicense
                  loading={loading}
                  data={businessUsers?.data[0]}
                />

                <div className="row d-flex align-items-center justify-content-between mt-3 py-3">
                  <div className="col d-flex align-items-center justify-content-start gap-3">
                    {
                      (businessUsers?.data[0]?.status === "PENDING_APPROVAL" || businessUsers?.data[0]?.status === "DEACTIVE" || businessUsers?.data[0]?.status === "REJECTED") && businessUsers?.data[0]?.role === "business" && (
                        <button
                          className="btn btn-verify-account"
                          onClick={() => verifyAccountButtonHandler()}
                          disabled={!isIataDiscountValid}
                          title={isIataDiscountValid ? undefined : "Enter a discount rate above 0% before verifying"}
                        >
                          Verify
                        </button>
                      )
                    }
                  </div>
                  <div className="col d-flex align-items-center justify-content-end gap-2">
                    <a
                      href={`mailto:${businessUsers?.data[0].businessDetail?.authorizedRepresentativeContact?.emailAddress}`}
                      className="btn btn-send-inquiry"
                    >
                      <div className="d-flex align-items-center justify-content-center gap-1">
                        <SendInquiryIcon />
                        Send Inquiry
                      </div>
                    </a>
                    {
                      businessUsers?.data[0]?.status === "PENDING_APPROVAL" && businessUsers?.data[0]?.role === "business" && (
                        <button
                          className="btn btn-reject-account"
                          onClick={() => rejectAccountButtonHandler()}
                        >
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            <RejectAccountIcon />
                            Decline Registration
                          </div>
                        </button>
                      )
                    }
                    {
                      businessUsers?.data[0]?.status === "ACTIVE" && businessUsers?.data[0]?.role === "business" && (
                        <button
                          className="btn btn-deactive-account"
                          onClick={() => deactiveAccountButtonHandler()}
                        >
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            Deactivate Account
                          </div>
                        </button>
                      )
                    }
                  </div>
                </div>
              </>
            )
          }
        </div>
      </main>
    </Layout >
  )
}

export default BusinessDetail;

